import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SearchIcon } from './searchIcon.js';
import { smallMediaQuery } from '../../../common/utilities/sizeHelpers';
import { translate } from '../../../common/translation/translate';
import { reduxConnect } from '../../../common';
import { getRecentSearchLocations, switchLocation } from '../../../common/actions/locationActions';
import debounce from 'lodash.debounce';
import { dataLayer } from '@weatherbug/common';
import { useKeydownEvent, keyCodes } from '../../../common/hooks/useKeydownEvent';
import { compose } from 'redux';
import { getLocationUrl, getRoutePath } from './utilities.js';
import ngDeps from '../../../common/utilities/ngDependencies.js';
import { resetServerTrafficCamData } from '../../../common/actions/trafficCameraActions';

const { searchLocationBySearchString, setLocationSuggestionIndex, clearLocationSearchSuggestions, changeLocation } = dataLayer.actions;

const mapStoreStateToProps = (storeState) => {
    const { locationBaseUrl, authId, secret, stdParameters, user, routePath } = storeState?.appState?.appState || {};
    const currRoutePath = routePath && getRoutePath(routePath);
    const { locationSuggestionIndex } = storeState?.locationSearch || {};
    const locationSearchResults = storeState?.locationSearch?.locationSearchResults || [];

    const locality = user?.ActiveLocation?.Locality;
    const cityName = locality?.CityName;
    const territoryName = locality?.TerritoryName;
    const displayCompositeName = locality?.DisplayCompositeName;

    return {
        cityTerritoryName: displayCompositeName || `${cityName}, ${territoryName}`,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        locationBaseUrl,
        authId,
        secret,
        stdParameters,
        locationSuggestionIndex,
        locationSearchResults,
        currRoutePath
    };
};

const mapDispatchToProps = {
    getRecentSearchLocations,
    searchLocationBySearchString,
    setLocationSuggestionIndex,
    clearLocationSearchSuggestions,
    switchLocation,
    changeLocation,
    resetServerTrafficCamData
};

export const LocationSearcherComponent = ({
    cityTerritoryName,
    locationBaseUrl,
    authId,
    secret,
    stdParameters,
    searchLocationBySearchString,
    toggleSuggestionPopup,
    setLocationSuggestionIndex,
    locationSuggestionIndex,
    locationSearchResults = [],
    clearLocationSearchSuggestions,
    resetServerTrafficCamData,
    latitude,
    longitude,
    switchLocation,
    changeLocation,
    currRoutePath
}) => {
    const [ locationInputHasFocus, setLocationInputHasFocus ] = useState(false);
    const [ currentSearchText, setCurrentSearchText ] = useState('');
    const inputRef = useRef();

    useEffect(()=>{
        setCurrentSearchText(locationSearchResults[locationSuggestionIndex]?.DisplayCompositeName);
    }, [ locationSuggestionIndex ]);

    useEffect(()=>{
        setCurrentSearchText('');
        toggleSuggestionPopup(false);
        inputRef.current.blur();
    }, [ latitude, longitude ]);

    useEffect(() => {
        if (currentSearchText === '') {
            clearLocationSearchSuggestions();
        }
    }, [currentSearchText]);

    const getSuggestedSearchResults = useCallback(debounce((searchText) => {
        if (authId && locationBaseUrl && secret) {
            searchLocationBySearchString(searchText, locationBaseUrl, authId, secret, stdParameters, true, true, 10);
        }
    }, 250, { leading: false, trailing: true }), [locationBaseUrl, authId, secret]);

    const onSearchInputChange = useCallback((e) => {
        const searchText = e.currentTarget.value;
        setCurrentSearchText(searchText);
        if (searchText?.length > 1) {
            getSuggestedSearchResults(searchText);
        }
        if (!locationInputHasFocus) {
            toggleSuggestionPopup(true);
        }
    }, [locationInputHasFocus, getSuggestedSearchResults, currentSearchText]);

    const onFocusChanged = useCallback((inputHasFocus)=>{
        // This timeout is to fix race conditions between losing focus and hiding the link buttons on suggested/recent locations
        const timeoutDuration = inputHasFocus ? 0 : 200;
        setTimeout(()=>{
            setLocationInputHasFocus(inputHasFocus);
            toggleSuggestionPopup(inputHasFocus);
        }, timeoutDuration);

        if (inputHasFocus) {
            getRecentSearchLocations();
        }
    });

    const escapePressed = useCallback(() => {
        setCurrentSearchText('');
    });

    const enterPressed = useCallback(() => {
        const selectedLocation = locationSearchResults[locationSuggestionIndex];
        if (selectedLocation) {
            resetServerTrafficCamData();
            const locationRouteString = selectedLocation?.RouteString || selectedLocation?.SlugName;
            const url = getLocationUrl(currRoutePath, locationRouteString);
            window.history.pushState('', '', url);
            ngDeps.$route.reload();
            setLocationInputHasFocus(false);
        }
    });

    const upArrowPressed = useCallback((event) => {
        if (currentSearchText?.length > 1) {
            setLocationSuggestionIndex(false);
            event.preventDefault();
            event.stopPropagation();
        }
    });

    const downArrowPressed = useCallback((event) => {
        if (currentSearchText?.length > 1) {
            setLocationSuggestionIndex(true);
            event.preventDefault();
            event.stopPropagation();
        }
    });

    useKeydownEvent(keyCodes.escape, escapePressed, inputRef, []);
    useKeydownEvent(keyCodes.enter, enterPressed, inputRef, [locationSearchResults, locationSuggestionIndex]);
    useKeydownEvent(keyCodes.upArrow, upArrowPressed, inputRef, [ currentSearchText ]);
    useKeydownEvent(keyCodes.downArrow, downArrowPressed, inputRef, [ currentSearchText ]);

    return (
        <Container>
            <LocationInputContainer>
                <LocationInput
                    id='locationInput'
                    type='text'
                    ref={inputRef}
                    value={currentSearchText || ''}
                    onChange={onSearchInputChange}
                    onFocus={()=>onFocusChanged(true)}
                    onBlur={()=>onFocusChanged(false)}
                    placeholder={locationInputHasFocus && cityTerritoryName ? translate('searchInputPlaceholder') : cityTerritoryName  }
                />
                <SearchIconContainer>
                    <SearchIcon />
                </SearchIconContainer>
            </LocationInputContainer>
        </Container>
    );
};

LocationSearcherComponent.displayName = 'LocationSearcher';

LocationSearcherComponent.propTypes = {
    cityTerritoryName: PropTypes.string,
    locationBaseUrl: PropTypes.string,
    authId: PropTypes.string,
    secret: PropTypes.string,
    stdParameters: PropTypes.object,
    locationSuggestionIndex: PropTypes.number,
    locationSearchResults: PropTypes.array,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    toggleSuggestionPopup: PropTypes.func.isRequired,
    clearLocationSearchSuggestions: PropTypes.func.isRequired,
    getRecentSearchLocations: PropTypes.func.isRequired,
    setLocationSuggestionIndex: PropTypes.func.isRequired,
    searchLocationBySearchString: PropTypes.func.isRequired,
    switchLocation: PropTypes.func.isRequired
};

export const LocationSearcher = compose(reduxConnect(mapStoreStateToProps, mapDispatchToProps), memo)(LocationSearcherComponent);

const Container = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    @media${smallMediaQuery} {
        flex: 1
    }
`;

const LocationInputContainer = styled.div`
    display: flex;
    align-items: center;
    @media${smallMediaQuery} {
        flex: 1
    }
`;

const LocationInput = styled.input`
    font-size: 16px;
    font-weight: 400;
    padding-top: 3px;
    padding-left: 10px;
    padding-right: 25px;
    padding-bottom: 5px;
    height: 38px;
    color: #435560;
    border: 1px solid #B0B7BC;
    background-color: #EEEEEE;
    border-radius: 2px;
    outline: none;
    ::placeholder {
        color: rgba(67,85,96,0.5);
    }
    @media${smallMediaQuery} {
        flex: 1
    }
`;

const SearchIconContainer = styled.div`
    margin-left: -22px;
    transform: rotate(270deg);
    cursor: pointer;
`;
